import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL as API } from '../config/api';

console.log('[Auth] API base:', API);

// Create auth context
const AuthContext = createContext(null);

// Token storage keys
const TOKEN_KEY = 'aureate_admin_token';
const TOKEN_EXPIRY_KEY = 'aureate_admin_token_expiry';

// Create a single global axios instance
const authAxios = axios.create({
  baseURL: API,
});

// Add auth token to requests - reads from localStorage each time
authAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    console.log('[AuthAxios] Request to:', config.url, 'Token present:', !!token);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - just log errors, don't auto-logout (that's handled by components)
authAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log('[AuthAxios] Request failed:', error.response?.status, error.message);
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    setIsAuthenticated(false);
  }, []);

  // Check if token is still valid
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

    console.log('[Auth] Checking auth, token present:', !!token);

    if (!token || !expiry) {
      setIsAuthenticated(false);
      setIsLoading(false);
      return false;
    }

    // Check if token has expired locally
    const expiryDate = new Date(expiry);
    if (expiryDate < new Date()) {
      console.log('[Auth] Token expired locally');
      logout();
      return false;
    }

    // Verify token with server
    try {
      const response = await axios.get(`${API}/auth/verify-token`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.valid) {
        console.log('[Auth] Token valid');
        setIsAuthenticated(true);
        setIsLoading(false);
        return true;
      } else {
        console.log('[Auth] Token invalid from server');
        logout();
        return false;
      }
    } catch (err) {
      console.log('[Auth] Token verification failed:', err.message);
      logout();
      return false;
    }
  }, [logout]);

  // Initialize auth state on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login with PIN
  const login = async (pin) => {
    setError(null);
    try {
      console.log('[Auth] Attempting login...');
      const response = await axios.post(`${API}/auth/verify-pin`, { pin });
      
      if (response.data.success) {
        console.log('[Auth] Login successful, storing token');
        localStorage.setItem(TOKEN_KEY, response.data.token);
        localStorage.setItem(TOKEN_EXPIRY_KEY, response.data.expires_at);
        setIsAuthenticated(true);
        return { success: true };
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid PIN';
      console.log('[Auth] Login failed:', message);
      setError(message);
      return { success: false, error: message };
    }
  };

  // Get the current token (for manual use if needed)
  const getToken = () => localStorage.getItem(TOKEN_KEY);

  const value = {
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    checkAuth,
    getToken,
    authAxios, // Axios instance with auth headers
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
